import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, Lock, Briefcase, User, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react'
import api from '@/shared/lib/api'
import { useAuthStore } from '@/core/stores/auth'

interface InviteInfo {
  email: string
  role: string
  workspace_name: string
  team_name: string | null
  inviter_name: string | null
  expired: boolean
}

export function OnboardingPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)

  const [info, setInfo] = useState<InviteInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [jobTitle, setJobTitle] = useState('')

  useEffect(() => {
    if (!token) return
    api.get(`/onboarding/invite/${token}`)
      .then(({ data }) => setInfo(data.data))
      .catch(() => setError('Invitation non trouvée ou expirée'))
      .finally(() => setLoading(false))
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas')
      return
    }
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères')
      return
    }
    if (!fullName.trim() || !jobTitle.trim()) {
      setError('Tous les champs sont requis')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const { data } = await api.post('/onboarding/accept', {
        token,
        password,
        full_name: fullName.trim(),
        job_title: jobTitle.trim(),
      })
      // Auto-login
      if (data.data?.access_token) {
        localStorage.setItem('access_token', data.data.access_token)
        await login(data.data.access_token)
        navigate('/home')
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la création du compte')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-ink-faint" />
      </div>
    )
  }

  if (!info || info.expired) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-brick-light flex items-center justify-center">
            <AlertCircle size={32} className="text-brick" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-ink mb-2">Invitation expirée</h1>
          <p className="text-sm text-ink-muted mb-6">
            Cette invitation n'est plus valide. Demandez à votre administrateur de vous envoyer une nouvelle invitation.
          </p>
          <button onClick={() => navigate('/login')}
            className="px-6 py-2.5 bg-onyx text-paper rounded-lg text-sm font-medium hover:bg-onyx-light transition-colors">
            Aller à la connexion
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gold-light flex items-center justify-center">
            <svg width="28" height="28" viewBox="0 0 44 44" fill="none">
              <circle cx="22" cy="22" r="8" stroke="#C9A84C" strokeWidth="2" fill="none"/>
              <circle cx="22" cy="22" r="14" stroke="#C9A84C" strokeWidth="1.2" opacity="0.4" fill="none"/>
              <circle cx="22" cy="22" r="20" stroke="#C9A84C" strokeWidth="0.8" opacity="0.2" fill="none"/>
              <circle cx="22" cy="22" r="3" fill="#C9A84C"/>
            </svg>
          </div>
          <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Bienvenue sur TremorApp</h1>
          <p className="text-sm text-ink-muted mt-2">
            {info.inviter_name
              ? <><strong>{info.inviter_name}</strong> vous invite à rejoindre </>
              : <>Vous êtes invité à rejoindre </>
            }
            <strong>{info.workspace_name}</strong>
            {info.team_name && <> — équipe <strong>{info.team_name}</strong></>}
          </p>
        </div>

        {/* Email badge */}
        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex items-center gap-2 bg-white border border-ink-border rounded-full px-4 py-2">
            <CheckCircle2 size={16} className="text-emerald-500" />
            <span className="text-sm text-ink font-medium">{info.email}</span>
            <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">vérifié</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-ink-border shadow-card p-6 space-y-5">
          {/* Full name */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Nom complet</label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                placeholder="Jean Dupont" required
                className="w-full pl-10 pr-4 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
            </div>
          </div>

          {/* Job title */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Poste / Fonction</label>
            <div className="relative">
              <Briefcase size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Responsable Marketing, Chef de projet, ..." required
                className="w-full pl-10 pr-4 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Mot de passe</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 caractères" required minLength={8}
                className="w-full pl-10 pr-4 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
            </div>
          </div>

          {/* Confirm password */}
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Confirmer le mot de passe</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Retapez votre mot de passe" required
                className="w-full pl-10 pr-4 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button type="submit" disabled={submitting}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-onyx text-paper rounded-lg text-sm font-semibold hover:bg-onyx-light transition-colors disabled:opacity-50">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
            {submitting ? 'Création du compte...' : 'Créer mon compte et accéder à TremorApp'}
          </button>

          <p className="text-[11px] text-ink-faint text-center">
            En créant votre compte, vous acceptez les <a href="/cgu" className="underline">CGU</a> et la <a href="/cgv" className="underline">politique de confidentialité</a>.
          </p>
        </form>
      </div>
    </div>
  )
}
